import React, { useState } from 'react';
import { PageLayout } from '../components/PageLayout';
import { PrimaryButton } from '../components/PrimaryButton';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../i18n/LanguageContext';
import styles from './WelcomePage.module.css';

interface WelcomePageProps {
  takeoutEnabled?: boolean;
  waitingCount?: number;
  estimatedMinutes?: number;
  takeoutWaitingCount?: number;
  takeoutEstimatedMinutes?: number;
  storeImage?: string | null;
  isAcceptingNow?: boolean;
  closedMessage?: string | null;
  onStartDineIn?: () => void;
  onStartTakeout?: () => void;
  onStart: () => void;
}

/**
 * 웰컴 페이지
 * - 언어 선택 → 매장/포장 선택(ON일 때) → 대기 등록
 */
export function WelcomePage({
  takeoutEnabled = false,
  waitingCount = 0,
  estimatedMinutes = 0,
  takeoutWaitingCount = 0,
  takeoutEstimatedMinutes = 0,
  storeImage = null,
  isAcceptingNow = true,
  closedMessage = null,
  onStartDineIn,
  onStartTakeout,
  onStart,
}: WelcomePageProps) {
  const { t } = useLanguage();
  const [selectedOrderType, setSelectedOrderType] = useState<'DINE_IN' | 'TAKEOUT' | null>(null);

  const handleStart = () => {
    if (takeoutEnabled) {
      if (selectedOrderType === 'TAKEOUT' && onStartTakeout) {
        onStartTakeout();
      } else if (onStartDineIn) {
        onStartDineIn();
      }
    } else {
      onStart();
    }
  };

  const canStart = isAcceptingNow && (!takeoutEnabled || selectedOrderType !== null);

  return (
    <PageLayout
      takeoutEnabled={takeoutEnabled}
      waitingCount={waitingCount}
      estimatedMinutes={estimatedMinutes}
      takeoutWaitingCount={takeoutWaitingCount}
      takeoutEstimatedMinutes={takeoutEstimatedMinutes}
      storeImage={storeImage}
    >
      <p className={styles.description}>
        {t('welcome_subtitle')}
      </p>

      {/* 언어 선택 */}
      <div className={styles.langArea}>
        <LanguageSelector />
      </div>

      {/* 매장/포장 선택 (ON일 때만) */}
      {takeoutEnabled && (
        <div className={styles.orderTypeArea}>
          <button
            className={`${styles.orderTypeBtn} ${selectedOrderType === 'DINE_IN' ? styles.orderTypeBtnActive : ''}`}
            onClick={() => setSelectedOrderType('DINE_IN')}
          >
            {t('order_dine_in')}
          </button>
          <button
            className={`${styles.orderTypeBtn} ${selectedOrderType === 'TAKEOUT' ? styles.orderTypeBtnActive : ''}`}
            onClick={() => setSelectedOrderType('TAKEOUT')}
          >
            {t('order_takeout')}
          </button>
        </div>
      )}

      {/* 접수 불가 안내 */}
      {!isAcceptingNow && closedMessage && (
        <div className={styles.closedNotice}>{closedMessage}</div>
      )}

      {/* 대기 등록 / 메뉴 선택 버튼 */}
      <div className={styles.buttonWrapper}>
        <PrimaryButton onClick={handleStart} disabled={!canStart}>
          {selectedOrderType === 'TAKEOUT'
            ? t('welcome_button_takeout')
            : t('welcome_button')}
        </PrimaryButton>
      </div>
    </PageLayout>
  );
}
